import React, { useEffect, useState, useCallback, useRef } from "react";

import { RefreshCw, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { cloudUsageApi } from "../../../lib/api/cloud-usage";
import { billingApi, PaymentMethod } from "../../../lib/api/billing";
import { AutoReplenishModal } from "./AutoReplenishModal";

interface AutoReplenishCardProps {
  className?: string;
}

const formatCardBrand = (brand: string): string => {
  const brands: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return brands[brand.toLowerCase()] || brand;
};

export const AutoReplenishCard: React.FC<AutoReplenishCardProps> = ({ className }) => {
  const { getIdToken } = useAuth();
  const { isAdminOrOwner } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Current settings
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(10);
  const [amount, setAmount] = useState(25);
  const [invoiceDefaultCard, setInvoiceDefaultCard] = useState<PaymentMethod | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [modalThreshold, setModalThreshold] = useState(10);
  const [modalAmount, setModalAmount] = useState(25);
  const [saving, setSaving] = useState(false);

  // Track if we should open modal after loading (for add_card return)
  const shouldOpenModalRef = useRef(false);

  const loadSettings = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required");
        return;
      }

      // Load balance settings and payment methods in parallel
      const [balance, pmResponse] = await Promise.all([
        cloudUsageApi.getBalance(token),
        billingApi.getPaymentMethods(token).catch(() => ({
          paymentMethods: [],
          subscriptionDefaultId: null,
          invoiceDefaultId: null,
        })),
      ]);

      setEnabled(balance.autoReplenish);
      setThreshold(balance.replenishThreshold);
      setAmount(balance.replenishAmount);

      // Find the invoice default card
      const defaultCard = pmResponse.paymentMethods.find(pm => pm.isInvoiceDefault) || null;
      setInvoiceDefaultCard(defaultCard);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      console.error("Error loading auto-replenish settings:", err);
      if (axiosErr.response?.status !== 404) {
        setError(axiosErr.response?.data?.message || "Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  // Initial load
  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Handle return from Stripe Checkout after adding card
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("add_card") === "success") {
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, "", newUrl);

      // Show success message
      toast.success("Card added successfully");

      // Mark that we should open modal after loading completes
      shouldOpenModalRef.current = true;
    }
  }, []);

  // Open modal after loading if returning from add card
  useEffect(() => {
    if (!loading && shouldOpenModalRef.current) {
      shouldOpenModalRef.current = false;
      openEnableModal();
    }
  }, [loading]);

  const openEnableModal = async (): Promise<void> => {
    setModalOpen(true);
    setModalLoading(true);
    setModalThreshold(threshold);
    setModalAmount(amount);

    try {
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        setModalOpen(false);
        return;
      }

      const pmResponse = await billingApi.getPaymentMethods(token);
      setPaymentMethods(pmResponse.paymentMethods);

      // Pre-select invoice default, or subscription default, or first card
      if (pmResponse.invoiceDefaultId) {
        setSelectedCardId(pmResponse.invoiceDefaultId);
      } else if (pmResponse.subscriptionDefaultId) {
        setSelectedCardId(pmResponse.subscriptionDefaultId);
      } else if (pmResponse.paymentMethods.length > 0) {
        setSelectedCardId(pmResponse.paymentMethods[0].id);
      } else {
        setSelectedCardId(null);
      }
    } catch (err) {
      console.error("Error loading payment methods:", err);
      toast.error("Failed to load payment methods");
    } finally {
      setModalLoading(false);
    }
  };

  const handleAddCard = async (): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const response = await billingApi.addCard(token, true);
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (err) {
      console.error("Error creating add card session:", err);
      toast.error("Failed to add card");
    }
  };

  const handleEnableSave = async (): Promise<void> => {
    if (!selectedCardId) {
      toast.error("Please select a payment method");
      return;
    }

    try {
      setSaving(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      // Set invoice default if different from current
      const currentDefault = paymentMethods.find(pm => pm.isInvoiceDefault);
      if (!currentDefault || currentDefault.id !== selectedCardId) {
        await billingApi.setInvoiceDefault(token, selectedCardId);
      }

      // Enable auto-replenish with settings
      await cloudUsageApi.updateAutoReplenish(token, {
        enabled: true,
        threshold: modalThreshold,
        amount: modalAmount,
      });

      // Update local state
      setEnabled(true);
      setThreshold(modalThreshold);
      setAmount(modalAmount);
      const selectedCard = paymentMethods.find(pm => pm.id === selectedCardId) || null;
      setInvoiceDefaultCard(selectedCard);

      setModalOpen(false);
      toast.success("Auto-replenish enabled");
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      console.error("Error enabling auto-replenish:", err);
      toast.error(axiosErr.response?.data?.message || "Failed to enable auto-replenish");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (): Promise<void> => {
    try {
      setSaving(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      await cloudUsageApi.updateAutoReplenish(token, { enabled: false });
      setEnabled(false);
      toast.success("Auto-replenish disabled");
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      console.error("Error disabling auto-replenish:", err);
      toast.error(axiosErr.response?.data?.message || "Failed to disable auto-replenish");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardContent className="py-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <RefreshCw className="h-4 w-4" />
              <span>Auto-Replenish</span>
              {enabled && (
                <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  Enabled
                </span>
              )}
            </div>
          </div>

          {enabled ? (
            /* Enabled state - show current settings */
            <div className="space-y-3">
              <div className="p-3 bg-muted/50 rounded-md space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">When balance drops below:</span>
                  <span className="font-medium">${threshold}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Add amount:</span>
                  <span className="font-medium">${amount}</span>
                </div>
                {invoiceDefaultCard && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Payment method:</span>
                    <span className="font-medium">
                      {formatCardBrand(invoiceDefaultCard.brand)} ****{invoiceDefaultCard.last4}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisable}
                  disabled={saving || !isAdminOrOwner}
                  className="text-destructive border-destructive/50 hover:bg-destructive/10"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Disable
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEnableModal}
                  disabled={!isAdminOrOwner}
                >
                  Change Settings
                </Button>
              </div>
            </div>
          ) : (
            /* Disabled state - show enable button */
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Automatically add funds when your balance is low to avoid service interruption.
              </p>
              <Button
                onClick={openEnableModal}
                disabled={!isAdminOrOwner}
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Enable Auto-Replenish
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AutoReplenishModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        enabled={enabled}
        loading={modalLoading}
        saving={saving}
        paymentMethods={paymentMethods}
        selectedCardId={selectedCardId}
        onSelectCard={setSelectedCardId}
        threshold={modalThreshold}
        onThresholdChange={setModalThreshold}
        amount={modalAmount}
        onAmountChange={setModalAmount}
        onAddCard={handleAddCard}
        onSave={handleEnableSave}
      />
    </>
  );
};
